import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMessageCircle, FiEdit, FiSearch } from "react-icons/fi";
import { MdVerified } from "react-icons/md";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2 },
  },
};

// Get avatar gradient class based on name
const getAvatarGradient = (name) => {
  if (!name) return "grad-1";
  const code = name.charCodeAt(0) % 5;
  return `grad-${code + 1}`;
};

// Format timestamp for display
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const messageDate = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) {
    return messageDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (messageDate.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return messageDate.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    });
  }
};

// Format lastSeen for sidebar (shorter format)
const formatLastSeenShort = (lastSeen) => {
  if (!lastSeen) return "";

  const now = new Date();
  const lastSeenDate = new Date(lastSeen);
  const diffMs = now - lastSeenDate;
  const diffMinutes = Math.floor(diffMs / 1000 / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return lastSeenDate.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    });
  }
};

const ChatSidebar = ({
  conversations,
  selectedConversation,
  onConversationSelect,
  onNewMessage,
  searchQuery,
  onSearchChange,
  conversationFilter,
  onFilterChange,
  totalUnread,
  unreadByConversation,
  typingUsers,
  loading,
  isHidden,
}) => {
  return (
    <motion.aside
      className={`msgx-sidebar ${isHidden ? "hidden" : ""}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="msgx-sidebar-header">
        <div className="msgx-sidebar-title">
          <h1>
            <span className="msgx-title-icon">
              <FiMessageCircle />
            </span>
            Messages
          </h1>
          <motion.button
            className="msgx-new-chat-btn"
            onClick={onNewMessage}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            title="New Message"
          >
            <FiEdit />
          </motion.button>
        </div>

        {/* Filter Tabs */}
        <div className="msgx-filter-tabs">
          <button
            className={`msgx-filter-tab ${
              conversationFilter === "all" ? "active" : ""
            }`}
            onClick={() => onFilterChange("all")}
          >
            All
          </button>
          <button
            className={`msgx-filter-tab ${
              conversationFilter === "online" ? "active" : ""
            }`}
            onClick={() => onFilterChange("online")}
          >
            Online
          </button>
          <button
            className={`msgx-filter-tab ${
              conversationFilter === "unread" ? "active" : ""
            }`}
            onClick={() => onFilterChange("unread")}
          >
            Unread
            {totalUnread > 0 && (
              <span className="msgx-filter-badge">{totalUnread}</span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="msgx-search">
          <FiSearch className="msgx-search-icon" />
          <input
            type="text"
            className="msgx-search-input"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      {/* Conversation List */}
      <motion.div
        className="msgx-conversation-list"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {loading && !conversations.length ? (
          <div className="msgx-loading">
            <div className="msgx-spinner" />
            <span className="msgx-loading-text">Loading conversations...</span>
          </div>
        ) : conversations.length === 0 ? (
          <div className="msgx-loading">
            <span className="msgx-loading-text">No conversations yet</span>
          </div>
        ) : (
          <AnimatePresence>
            {conversations.map((conversation) => {
              const otherParticipant = conversation.otherParticipant;
              // Use ?? (nullish coalescing) so 0 is treated as valid, not falsy
              const unreadCount =
                unreadByConversation[conversation._id] ??
                conversation.unreadCount ??
                0;
              const isTyping = typingUsers.has(otherParticipant?._id);

              return (
                <motion.div
                  key={conversation._id}
                  className={`msgx-conversation-item ${
                    selectedConversation?._id === conversation._id
                      ? "active"
                      : ""
                  }`}
                  onClick={() => onConversationSelect(conversation)}
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  layout
                >
                  {/* Avatar */}
                  <div className="msgx-avatar">
                    {otherParticipant?.profilePhotoUrl ? (
                      <img
                        src={otherParticipant.profilePhotoUrl}
                        alt={otherParticipant?.name || "User"}
                        className="msgx-avatar-img"
                      />
                    ) : (
                      <div
                        className={`msgx-avatar-initials ${getAvatarGradient(
                          otherParticipant?.name
                        )}`}
                      >
                        {otherParticipant?.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                    )}
                    <span
                      className={`msgx-status-dot ${
                        otherParticipant?.isOnline ? "online" : "offline"
                      }`}
                      title={
                        otherParticipant?.isOnline
                          ? "Online"
                          : otherParticipant?.lastSeen
                          ? `Last seen ${formatLastSeenShort(
                              otherParticipant.lastSeen
                            )}`
                          : "Offline"
                      }
                    />
                  </div>

                  {/* Info */}
                  <div className="msgx-conversation-info">
                    <div className="msgx-conversation-top">
                      <span className="msgx-conversation-name">
                        {otherParticipant?.name || "Unknown User"}
                        {otherParticipant?.isVerified && (
                          <MdVerified className="msgx-verified-badge" />
                        )}
                      </span>
                      <span className="msgx-conversation-time">
                        {formatTimestamp(conversation.lastActivity)}
                      </span>
                    </div>

                    {(otherParticipant?.professionalRole ||
                      otherParticipant?.researchField) && (
                      <span className="msgx-conversation-role">
                        {otherParticipant?.professionalRole ||
                          otherParticipant?.researchField}
                      </span>
                    )}

                    <div className="msgx-conversation-preview">
                      <span className="msgx-preview-text">
                        {isTyping ? (
                          <span className="msgx-typing-text">typing...</span>
                        ) : conversation.lastMessage?.content ? (
                          conversation.lastMessage.content
                        ) : (
                          <em style={{ opacity: 0.6 }}>Start a conversation</em>
                        )}
                      </span>
                      {unreadCount > 0 && (
                        <span className="msgx-unread-badge">{unreadCount}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </motion.div>
    </motion.aside>
  );
};

export default ChatSidebar;
